import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import {
    Plus,
    Upload,
    MapPin,
    User,
    CheckCircle2,
    XCircle
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';
import { User as UserType } from "../../types";

interface AddLocationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    location?: any;
}

const AddLocationModal: React.FC<AddLocationModalProps> = ({ isOpen, onClose, onRefresh, location }) => {
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        managerId: '',
        isActive: true
    });

    const [users, setUsers] = useState<UserType[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            if (location) {
                setFormData({
                    name: location.name || '',
                    address: location.address || '',
                    managerId: location.manager?.id?.toString() || '',
                    isActive: location.isActive !== undefined ? location.isActive : true
                });
            } else {
                setFormData({
                    name: '',
                    address: '',
                    managerId: '',
                    isActive: true
                });
            }
        }
    }, [isOpen, location]);

    const fetchUsers = async () => {
        try {
            const data = await api.get('/api/users?all_users=true');
            setUsers(data.users);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Location name is required');
            return;
        }

        try {
            setLoading(true);
            if (location) {
                await api.patch(`/api/inventory/locations/${location.id}`, formData);
                toast.success('Location updated successfully');
            } else {
                await api.post('/api/inventory/locations', formData);
                toast.success('Location created successfully');
            }
            onRefresh();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save location');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-[32px] p-0 overflow-hidden border-none shadow-2xl">
                <div className="p-6 sm:p-8 space-y-6 max-h-[90vh] overflow-y-auto custom-scrollbar">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 text-xl sm:text-2xl font-bold">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                <MapPin className="text-blue-600 sm:w-5 sm:h-5" size={18} />
                            </div>
                            {location ? 'Edit Location' : 'Add New Location'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-5">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Location Name *</label>
                            <Input
                                placeholder="e.g., Head Office, Warehouse A"
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4 font-medium"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Address</label>
                            <Input
                                placeholder="123 Example St, City, Country"
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4 font-medium"
                                value={formData.address}
                                onChange={e => setFormData({ ...formData, address: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Location Manager</label>
                            <Select value={formData.managerId} onValueChange={val => setFormData({ ...formData, managerId: val })}>
                                <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4 shadow-sm text-sm">
                                    <SelectValue placeholder="Select a manager" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl">
                                    {users.map(u => (
                                        <SelectItem key={u.id} value={u.id.toString()} className="text-sm">
                                            {u.name} <span className="text-gray-400 text-[10px] ml-1">({u.jobRole})</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="pt-2">
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                                className={cn(
                                    "w-full flex items-center justify-between p-4 rounded-2xl border transition-all",
                                    formData.isActive
                                        ? "bg-green-50/50 border-green-100 text-green-700"
                                        : "bg-gray-50/50 border-gray-100 text-gray-400"
                                )}
                            >
                                <div className="flex items-center gap-3">
                                    {formData.isActive ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                                    <span className="font-bold">Location is Active</span>
                                </div>
                                <div className={cn(
                                    "w-10 h-5 rounded-full relative transition-all duration-300",
                                    formData.isActive ? "bg-green-500" : "bg-gray-300"
                                )}>
                                    <div className={cn(
                                        "absolute top-1 w-3 h-3 bg-white rounded-full transition-all duration-300",
                                        formData.isActive ? "left-6" : "left-1"
                                    )} />
                                </div>
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button
                            variant="outline"
                            className="order-2 sm:order-1 flex-1 rounded-2xl h-12 font-bold border-gray-100 hover:bg-gray-50"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="order-1 sm:order-2 flex-1 rounded-2xl h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold flex items-center justify-center gap-2 shadow-lg shadow-slate-900/10"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                location ? 'Update Location' : 'Create Location'
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddLocationModal;
